/**
 * Planned → Cashflow forecast (TMMAPP-0112, the 0101 amendment: `CashflowPanel` retired). The shared
 * `ForecastCard` on the selected scope's one projection (MM-D-53) — combined, an account or an
 * envelope — with the virtual account's KPI set: Balance today, Projected balance with its delta over
 * the horizon, and Lowest point "{date} · after {event}", every figure as served (the scope's own
 * series, `endMinor`, `minimum`), never recomputed. The scope picker sits in the filter slot and the
 * series table (`PlanTable`) in the list slot; its sheet and editors are the one series host
 * (`useSeriesHost`). A drill-down is the same endpoint with a narrower window.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Stack, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { ForecastCard, type ForecastKpi } from '@/features/cashflow/ForecastCard';
import { HORIZON_SCHEMA } from '@/features/cashflow/horizon';
import { PlanTable, type PlanTableLabels } from '@/features/cashflow/PlanTable';
import { scopeKey, type DrillDown, type ProjectionScope } from '@/features/cashflow/types';
import { useProjection } from '@/features/cashflow/useProjection';
import { useSeriesHost } from '@/features/cashflow/useSeriesHost';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';

interface PlannedCashflowProps {
  scope: ProjectionScope;
  /** The scope's own currency: an account's, an envelope's virtual account owner's, or the operating one. */
  currency: string | null;
  /** The forecast's operating currency (the table's remainder): an account's `operatingCurrency`, else `currency`. */
  operatingCurrency: string | null;
  labels: PlanTableLabels;
  /** The scope picker, in the card's filter slot. */
  filter: ReactNode;
  onShowSeries: (seriesId: number) => void;
  onNotify: (message: string) => void;
  /** After a write from the sheet or an editor, beside the projection's reload: the page's labels. */
  onChanged?: () => void | Promise<void>;
}

export function PlannedCashflow({
  scope,
  currency,
  operatingCurrency,
  labels,
  filter,
  onShowSeries,
  onNotify,
  onChanged,
}: PlannedCashflowProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const [{ h: horizon }] = useUrlState(HORIZON_SCHEMA);
  const key = scopeKey(scope);
  const [drill, setDrill] = useState<DrillDown | null>(null);
  const [prev, setPrev] = useState({ key, horizon });
  if (prev.key !== key || prev.horizon !== horizon) {
    setPrev({ key, horizon });
    if (drill !== null) setDrill(null);
  }
  const { status, error, full, table, fromToday, reload } = useProjection(scope, horizon, drill);
  const host = useSeriesHost({
    currency,
    onChanged: async () => {
      await Promise.all([reload(), onChanged?.()]);
    },
    onNotify,
  });

  if (status === 'loading' && full === null) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={320} />
        <Skeleton variant="rounded" height={160} />
      </Stack>
    );
  }
  if (status === 'error' && full === null) {
    return (
      <ErrorState
        variant="section"
        error={error}
        what={t('errors.what.cashflow')}
        onRetry={() => void reload()}
      />
    );
  }
  if (full === null) return null;

  const view = table ?? full;
  const own = full.chartSeries[0];
  const startMinor = own?.points[0]?.balanceMinor ?? null;
  const endMinor = full.endMinor;
  const delta = startMinor !== null && endMinor !== null ? endMinor - startMinor : null;
  const lowest = full.minimum;
  const cause = lowest?.cause?.payee ?? null;
  // The combined view's per-account lines are context: drawn from `lg` up, as before.
  const chart = full.chartSeries.filter((series) => lg || series.faint !== true);

  const kpis: ForecastKpi[] = [
    {
      id: 'today',
      label: t('forecast.balanceToday'),
      value: <MoneyText minor={startMinor} currency={currency} variant="figureSm" />,
    },
    {
      id: 'projected',
      label: t('forecast.projected'),
      value: <MoneyText minor={endMinor} currency={currency} variant="figureSm" />,
      ...(delta !== null
        ? {
            note: (
              <Typography
                variant="caption"
                component="span"
                data-delta=""
                sx={{ color: delta < 0 ? tokens.moneyNegative : tokens.moneyPositive }}
              >
                {t('forecast.overHorizon', {
                  delta: formatMoneyMinor(delta, currency, locale, true),
                })}
              </Typography>
            ),
          }
        : {}),
    },
    ...(lowest !== null
      ? [
          {
            id: 'lowest',
            label: t('forecast.lowestPoint'),
            value: <MoneyText minor={lowest.balanceMinor} currency={currency} variant="figureSm" />,
            note:
              cause !== null
                ? t('forecast.lowestCause', {
                    date: formatCivilDate(lowest.date, locale),
                    payee: cause,
                  })
                : formatCivilDate(lowest.date, locale),
          },
        ]
      : []),
  ];

  const fxList = full.fxUnavailable
    .map((row) =>
      t('cashflow.fxPair', { base: row.base, quote: row.quote, count: row.omittedSlices }),
    )
    .join(', ');

  return (
    <>
      <ForecastCard
        label={t('cashflow.region')}
        fromToday={fromToday}
        from={full.from}
        to={full.to}
        chart={{
          series: chart,
          events: full.events,
          register: full.register,
          currency,
          minimum: null,
          onDrill: setDrill,
        }}
        kpis={kpis}
        // The combined total counts pending accounts from 0 (TMMAPP-0123 item 19a).
        note={
          full.openingPendingAccountIds.length > 0
            ? t('accounts.openingPendingNote', { count: full.openingPendingAccountIds.length })
            : undefined
        }
        filter={filter}
        list={
          <Stack spacing={2}>
            {fxList.length > 0 && (
              <Alert severity="info">{t('cashflow.fxUnavailable', { list: fxList })}</Alert>
            )}
            <PlanTable
              register={view.register}
              slices={view.slices}
              currency={currency}
              operatingCurrency={operatingCurrency}
              owner
              labels={labels}
              fullFrom={full.from}
              fullTo={full.to}
              drill={drill}
              onDrill={setDrill}
              onWiden={() => setDrill(null)}
              onOpenOccurrence={(seriesId, originalOn) =>
                void host.openOccurrence(seriesId, originalOn)
              }
              onShowSeries={onShowSeries}
              onEditFollowing={(seriesId, originalOn) =>
                void host.openEditor('following', seriesId, originalOn)
              }
              onEditSeries={(seriesId) => void host.openEditor('series', seriesId)}
            />
          </Stack>
        }
      />
      {host.node}
    </>
  );
}
